
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

interface ConsentOption {
  id: string;
  label: {
    de: string;
    en: string;
    fr: string;
    it: string;
  };
  description: {
    de: string;
    en: string;
    fr: string;
    it: string;
  };
  type: 'analytics_storage' | 'ad_storage' | 'functionality_storage' | 'personalization_storage';
  required?: boolean;
}

const ConsentManager = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { currentLanguage } = useLanguage();
  
  const [consentChoices, setConsentChoices] = useState({
    analytics_storage: false,
    ad_storage: false,
    functionality_storage: false,
    personalization_storage: false,
  });
  
  const consentOptions: ConsentOption[] = [
    {
      id: 'analytics',
      label: {
        de: 'Analytics Cookies',
        en: 'Analytics Cookies',
        fr: 'Cookies d\'analyse',
        it: 'Cookie di analisi',
      },
      description: {
        de: 'Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.',
        en: 'Help us understand how visitors interact with our website.',
        fr: 'Nous aident à comprendre comment les visiteurs interagissent avec notre site.',
        it: 'Ci aiutano a capire come i visitatori interagiscono con il nostro sito.'
      },
      type: 'analytics_storage'
    },
    {
      id: 'ads',
      label: {
        de: 'Werbe-Cookies',
        en: 'Advertising Cookies',
        fr: 'Cookies publicitaires',
        it: 'Cookie pubblicitari',
      },
      description: {
        de: 'Werden verwendet, um personalisierte Werbung anzuzeigen.',
        en: 'Used to serve personalized advertisements.',
        fr: 'Utilisés pour diffuser des publicités personnalisées.',
        it: 'Utilizzati per mostrare pubblicità personalizzate.'
      },
      type: 'ad_storage'
    },
    {
      id: 'functionality',
      label: {
        de: 'Funktionelle Cookies',
        en: 'Functional Cookies',
        fr: 'Cookies fonctionnels',
        it: 'Cookie funzionali',
      },
      description: {
        de: 'Ermöglichen verbesserte Funktionalität und Personalisierung.',
        en: 'Enable enhanced functionality and personalization.',
        fr: 'Permettent des fonctionnalités et une personnalisation améliorées.',
        it: 'Consentono funzionalità e personalizzazione avanzate.'
      },
      type: 'functionality_storage'
    },
    {
      id: 'personalization',
      label: {
        de: 'Personalisierungs-Cookies',
        en: 'Personalization Cookies',
        fr: 'Cookies de personnalisation',
        it: 'Cookie di personalizzazione',
      },
      description: {
        de: 'Ermöglichen eine personalisierte Nutzererfahrung.',
        en: 'Enable a personalized user experience.',
        fr: 'Permettent une expérience utilisateur personnalisée.',
        it: 'Consentono un\'esperienza utente personalizzata.'
      },
      type: 'personalization_storage'
    }
  ];
  
  const texts = {
    de: {
      title: 'Cookie-Einstellungen',
      text: 'Wir verwenden Cookies. Für Statistik und personalisierte Werbung benötigen wir Ihre Zustimmung. Sie können Ihre Auswahl jederzeit ändern.',
      accept: 'Alle akzeptieren',
      reject: 'Nur notwendige',
      customize: 'Anpassen',
      save: 'Einstellungen speichern',
      settings: 'Cookie-Einstellungen',
      settingsDescription: 'Gemäss revFADP (Sept 2023) benötigen wir für personalisierte Werbung Ihre ausdrückliche Einwilligung. Funktionale Cookies können Sie optional deaktivieren.',
      required: 'Erforderlich',
      revfdpNote: 'Diese Einstellungen entsprechen dem revidierten Datenschutzgesetz (revFADP) der Schweiz.'
    },
    en: {
      title: 'Cookie Settings',
      text: 'We use cookies. Your consent is required for analytics and personalized ads. You can change your choice anytime.',
      accept: 'Accept all',
      reject: 'Essential only',
      customize: 'Customize',
      save: 'Save settings',
      settings: 'Cookie Settings',
      settingsDescription: 'According to Swiss data protection law, explicit consent is required for personalized advertising. Functional cookies are optional.',
      required: 'Required',
      revfdpNote: 'These settings comply with Swiss data protection law (revFADP).'
    },
    fr: {
      title: 'Paramètres des cookies',
      text: 'Nous utilisons des cookies. Pour les statistiques et la publicité personnalisée, votre consentement est requis. Vous pouvez modifier votre choix à tout moment.',
      accept: 'Tout accepter',
      reject: 'Nécessaires seulement',
      customize: 'Personnaliser',
      save: 'Sauvegarder',
      settings: 'Paramètres des cookies',
      settingsDescription: 'Selon la loi suisse sur la protection des données (revFADP Sept 2023), un consentement explicite est requis pour la publicité personnalisée.',
      required: 'Obligatoire',
      revfdpNote: 'Ces paramètres sont conformes à la loi suisse sur la protection des données (revFADP).'
    },
    it: {
      title: 'Impostazioni cookie',
      text: 'Utilizziamo cookie. Per le statistiche e la pubblicità personalizzata è necessario il consenso. Puoi modificare la scelta in qualsiasi momento.',
      accept: 'Accetta tutti',
      reject: 'Solo essenziali',
      customize: 'Personalizza',
      save: 'Salva impostazioni',
      settings: 'Impostazioni cookie',
      settingsDescription: 'Secondo la legge svizzera sulla protezione dei dati (revFADP), è richiesto il consenso esplicito per la pubblicità personalizzata.',
      required: 'Obbligatorio',
      revfdpNote: 'Queste impostazioni sono conformi alla legge svizzera sulla protezione dei dati (revFADP).'
    }
  };
  
  const t = texts[currentLanguage];
  
  useEffect(() => {
    // Check if user has already set consent preferences
    const consentState = localStorage.getItem('consentPreferences');
    
    if (!consentState) {
      // Show the banner after a short delay if no preferences found
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else {
      // Apply saved consent preferences
      try {
        const savedConsent = JSON.parse(consentState);
        setConsentChoices(savedConsent);
        updateGoogleConsent(savedConsent);
      } catch (e) {
        console.error('Error parsing consent preferences', e);
      }
    }
  }, []);
  
  const saveConsent = (preferences: typeof consentChoices) => {
    // Save to localStorage
    localStorage.setItem('consentPreferences', JSON.stringify(preferences));
    
    // Update Google Consent Mode
    updateGoogleConsent(preferences);
    
    // Hide banner
    setShowBanner(false);
    setShowSettings(false);
  };
  
  const updateGoogleConsent = (preferences: typeof consentChoices) => {
    // Access the global updateConsent function
    if (window.updateConsent) {
      window.updateConsent({
        'analytics_storage': preferences.analytics_storage ? 'granted' : 'denied',
        'ad_storage': preferences.ad_storage ? 'granted' : 'denied',
        'functionality_storage': preferences.functionality_storage ? 'granted' : 'denied',
        'personalization_storage': preferences.personalization_storage ? 'granted' : 'denied',
        'security_storage': 'granted' // Always granted
      });
    }
  };
  
  const acceptAllCookies = () => {
    const allConsented = {
      analytics_storage: true,
      ad_storage: true,
      functionality_storage: true,
      personalization_storage: true
    };
    setConsentChoices(allConsented);
    saveConsent(allConsented);
  };
  
  const rejectAllCookies = () => {
    const allRejected = {
      analytics_storage: false,
      ad_storage: false,
      functionality_storage: false,
      personalization_storage: false
    };
    setConsentChoices(allRejected);
    saveConsent(allRejected);
  };
  
  const openSettings = () => {
    setShowSettings(true);
  };
  
  const handleCheckboxChange = (type: keyof typeof consentChoices) => {
    setConsentChoices(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  
  const handleSaveSettings = () => {
    saveConsent(consentChoices);
  };
  
  const openConsentBanner = () => {
    setShowBanner(true);
  };
  
  // Attach to window for external access
  useEffect(() => {
    window.openConsentManager = openConsentBanner;
    return () => {
      delete window.openConsentManager;
    };
  }, []);
  
  if (!showBanner && !showSettings) return null;
  
  return (
    <>
      {/* Cookie Banner */}
      {showBanner && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1A1F2C]/95 backdrop-blur-md border-t border-purple-500/30 shadow-lg shadow-purple-800/20 p-4 animate-in slide-in-from-bottom">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-1">{t.title}</h3>
                <p className="text-sm text-gray-300 pr-4">{t.text}</p>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-purple-500/30 text-gray-300 hover:bg-purple-900/20 hover:text-white"
                  onClick={rejectAllCookies}
                >
                  {t.reject}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-purple-500/30 text-gray-300 hover:bg-purple-900/20 hover:text-white"
                  onClick={openSettings}
                >
                  {t.customize}
                </Button>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={acceptAllCookies}
                >
                  {t.accept}
                </Button>
                <button
                  onClick={() => setShowBanner(false)}
                  className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-purple-900/20 transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Detailed Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-[#1A1F2C] text-white border border-purple-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">{t.settings}</DialogTitle>
            <DialogDescription className="text-gray-300">
              {t.settingsDescription}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-4">
            {consentOptions.map((option) => (
              <div key={option.id} className="flex items-start space-x-3 p-3 rounded-md border border-gray-800 hover:bg-gray-800/30 transition-colors">
                <Checkbox
                  id={option.id}
                  checked={consentChoices[option.type]}
                  onCheckedChange={() => handleCheckboxChange(option.type)}
                  className="mt-1 data-[state=checked]:bg-purple-600 data-[state=checked]:text-white border-gray-500"
                />
                <div className="space-y-1">
                  <label
                    htmlFor={option.id}
                    className="text-sm font-medium leading-none text-white flex items-center"
                  >
                    {option.label[currentLanguage as keyof typeof option.label]}
                    {option.required && (
                      <span className="ml-2 text-xs text-purple-400 px-2 py-0.5 bg-purple-900/30 rounded-full">
                        {t.required}
                      </span>
                    )}
                  </label>
                  <p className="text-xs text-gray-400">{option.description[currentLanguage as keyof typeof option.description]}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowSettings(false)}
              className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSettings}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {t.save}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Add type declaration for global window object
declare global {
  interface Window {
    updateConsent: (settings: Record<string, string>) => void;
    openConsentManager: () => void;
  }
}

export default ConsentManager;
